import { NgClass, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateModule } from "@ngx-translate/core";
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription, filter } from 'rxjs';
import { Building } from '../../models/building.model';
import { CardReaderService, CardReaderServiceStatus } from "../../services/card-reader.service";

@Component({
  selector: 'app-card-indicator',
  standalone: true,
	imports: [
    NgIf,
    TranslateModule,
    ButtonModule,
    NgClass
],
  templateUrl: './card-indicator.component.html',
  styleUrl: './card-indicator.component.scss'
})
export class CardIndicatorComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() selectedBuilding: Building | null = null;
  @Input() active: boolean = false;
  
  @Output() cardScanned: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('iframeContainer') serviceIframeContainer: ElementRef<HTMLElement>;

  private cardReadSubscription?: Subscription;
  private cardReadErrorSubscription?: Subscription;
  private serviceStatusSubscription?: Subscription;
  private routerSubscription?: Subscription;
  private isOnSemaphorePage: boolean = false;
  cardReaderActive: boolean = false;
  cardReaderStatus: CardReaderServiceStatus | null = null;
  reconnecting: boolean = false;
  connecting: boolean = false;
  
  CardReaderServiceStatus = CardReaderServiceStatus;

  constructor(
    public cardReaderService: CardReaderService,
    private renderer: Renderer2,
    private dialogService: DialogService,
    private router: Router
  ) {
    // Check initial route
    this.checkCurrentRoute();
    
    // Subscribe to route changes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const wasOnSemaphorePage = this.isOnSemaphorePage;
        this.checkCurrentRoute();
        
        // If we navigated away from semaphore page, cleanup
        if (wasOnSemaphorePage && !this.isOnSemaphorePage) {
          this.cleanup();
        }
      });
  }
  
  private checkCurrentRoute(): void {
    const url = this.router.url;
    this.isOnSemaphorePage = url.includes('/semaphor') || url.endsWith('/semaphor');
  }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    const buildingChanged = !!changes['selectedBuilding'] && !changes['selectedBuilding'].firstChange;
    const activeChanged = !!changes['active'] && !changes['active'].firstChange;
    if ((buildingChanged || activeChanged) && this.serviceIframeContainer) {
      if (this.active && this.isOnSemaphorePage) {
        this.cleanup();
        this.initializeCardReader();
      } else {
        this.cleanup();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.active && !this.cardReaderActive && this.isOnSemaphorePage) {
      this.initializeCardReader();
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.cardReadSubscription) {
      this.cardReadSubscription.unsubscribe();
      this.cardReadSubscription = undefined;
    }
    if (this.cardReadErrorSubscription) {
      this.cardReadErrorSubscription.unsubscribe();
      this.cardReadErrorSubscription = undefined;
    }
    if (this.serviceStatusSubscription) {
      this.serviceStatusSubscription.unsubscribe();
      this.serviceStatusSubscription = undefined;
    }

    if (this.cardReaderActive) {
      this.cardReaderService.cancelRead().catch(error => {
        //console.error('Error canceling card read:', error);
      });
      this.cardReaderActive = false;
    }

    this.cardReaderService.disconnect();
    this.cardReaderStatus = null;
  }

  private async initializeCardReader(): Promise<void> {
    if (!this.isOnSemaphorePage) {
      //console.log('Card reader initialization skipped - not on semaphore page');
      return;
    }
    
    try {
      //console.log('Initializing card reader...');
      this.connecting = true;
      this.cardReaderStatus = null;

      let connected = await this.cardReaderService.testConnection(true);
      
      if (!connected && this.serviceIframeContainer) {
        //console.log('Connection failed, attempting to launch application...');
        connected = await this.cardReaderService.launchApplication(
          this.serviceIframeContainer.nativeElement,
          this.renderer,
          10
        );
      }
      
      if (connected) {
        //console.log('Card reader connected, starting continuous read...');
        const beginReadResult = await this.cardReaderService.beginRead();
        
        if (beginReadResult.success) {
          this.cardReaderActive = true;
          
          this.cardReadSubscription = this.cardReaderService.listenCardRead().subscribe({
            next: (cardSerial: string) => {
              //console.log('Card detected:', cardSerial);
              this.cardScanned.emit(cardSerial);
            },
            error: (error) => {
              //console.error('Error in card read subscription:', error);
            }
          });

          this.cardReadErrorSubscription = this.cardReaderService.listenCardReadError().subscribe({
            next: () => {
              //console.log('Card read error occurred');
            },
            error: (error) => {
              //console.error('Error in card read error subscription:', error);
            }
          });

          this.serviceStatusSubscription = this.cardReaderService.listenServiceStatus().subscribe({
            next: (status: CardReaderServiceStatus) => {
              //console.log('Card reader service status:', status);
              this.cardReaderStatus = status;
              this.connecting = false;
              if (status !== CardReaderServiceStatus.CARD_READER_CONNECTED) {
                this.cardReaderActive = false;
              } else {
                this.cardReaderActive = true;
              }
            },
            error: (error) => {
              //console.error('Error in service status subscription:', error);
              this.connecting = false;
            }
          });
          
          this.cardReaderStatus = this.cardReaderService.serviceStatus;
          this.connecting = false;
        } else {
          //console.warn('Failed to start card reader continuous read');
          this.cardReaderStatus = CardReaderServiceStatus.CARD_READER_SOCKET_ERROR;
          this.connecting = false;
        }
      } else {
        //console.warn('Failed to connect to card reader');
        this.cardReaderStatus = CardReaderServiceStatus.CARD_READER_SOCKET_ERROR;
        this.connecting = false;
      }
    } catch (error) {
      //console.error('Error initializing card reader:', error);
      this.cardReaderStatus = CardReaderServiceStatus.CARD_READER_SOCKET_ERROR;
      this.connecting = false;
    }
  }
  
  getCardReaderStatusMessage(): string {
    if (!this.active) return '';

    if (this.connecting || this.cardReaderStatus === null) {
      return 'Pripája sa k čítačke kariet...';
    }
    
    switch (this.cardReaderStatus) {
      case CardReaderServiceStatus.CARD_READER_CONNECTED:
        return 'Čítačka kariet pripojená';
      case CardReaderServiceStatus.CARD_READER_SOCKET_ERROR:
        return 'Chyba pripojenia k čítačke kariet';
      case CardReaderServiceStatus.CARD_READER_DEVICE_DISCONNECTED:
        return 'Čítačka kariet odpojená';
      case CardReaderServiceStatus.OS_NOT_SUPPORTED:
        return 'Čítačka kariet nie je podporovaná';
      default:
        return 'Pripája sa k čítačke kariet...';
    }
  }
  
  getCardReaderStatusIcon(): string {
    switch (this.cardReaderStatus) {
      case CardReaderServiceStatus.CARD_READER_CONNECTED:
        return 'pi-check-circle';
      case CardReaderServiceStatus.CARD_READER_SOCKET_ERROR:
      case CardReaderServiceStatus.CARD_READER_DEVICE_DISCONNECTED:
        return 'pi-times-circle';
      case CardReaderServiceStatus.OS_NOT_SUPPORTED:
        return 'pi-exclamation-triangle';
      default:
        return 'pi-spinner';
    }
  }

  shouldSpinIcon(): boolean {
    if (this.connecting || this.cardReaderStatus === null) {
      return true;
    }
    
    return this.cardReaderStatus !== CardReaderServiceStatus.CARD_READER_CONNECTED && 
           this.cardReaderStatus !== CardReaderServiceStatus.CARD_READER_SOCKET_ERROR && 
           this.cardReaderStatus !== CardReaderServiceStatus.CARD_READER_DEVICE_DISCONNECTED && 
           this.cardReaderStatus !== CardReaderServiceStatus.OS_NOT_SUPPORTED;
  }
  
  getCardReaderStatusSeverity(): 'success' | 'warn' | 'danger' | 'info' | 'connecting' {
    if (this.connecting || this.cardReaderStatus === null) {
      return 'connecting';
    }
    
    switch (this.cardReaderStatus) {
      case CardReaderServiceStatus.CARD_READER_CONNECTED:
        return 'success';
      case CardReaderServiceStatus.CARD_READER_SOCKET_ERROR:
      case CardReaderServiceStatus.CARD_READER_DEVICE_DISCONNECTED:
        return 'danger';
      case CardReaderServiceStatus.OS_NOT_SUPPORTED:
        return 'warn';
      default:
        return 'info';
    }
  }

  async reconnectCardReader(): Promise<void> {
    if (this.reconnecting || !this.active || !this.isOnSemaphorePage) {
      return;
    }
    this.reconnecting = true;
    try {
      this.cleanup();
      await this.initializeCardReader();
    } finally {
      this.reconnecting = false;
    }
  }

  async openManualDialog(): Promise<void> {
    const { InstallationDialogComponent } = await import('../installation-dialog/installation-dialog.component');
    this.dialogService.open(InstallationDialogComponent, {
      header: 'Inštalačný manuál',
      width: '80vw',
      modal: true,
      dismissableMask: true,
    });
  }
}
